import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useAdminAuth } from '@/hooks/useAdminAuth';

const navItems = [
  { label: 'Dashboard', icon: 'ri-dashboard-line', path: '/admin' },
  { label: 'Hero', icon: 'ri-image-line', path: '/admin/hero' },
  { label: 'Seções da Home', icon: 'ri-layout-line', path: '/admin/sections/home' },
  { label: 'Páginas Internas', icon: 'ri-pages-line', path: '/admin/sections/pages' },
  { label: 'Logomarca', icon: 'ri-trademark-line', path: '/admin/logo' },
  { label: 'Cores do Site', icon: 'ri-palette-line', path: '/admin/colors' },
  { label: 'Footer', icon: 'ri-layout-bottom-line', path: '/admin/footer' },
  { label: 'Biblioteca de Mídia', icon: 'ri-image-2-line', path: '/admin/media' },
  { label: 'Configurações', icon: 'ri-settings-3-line', path: '/admin/settings' },
];

export default function AdminLayout() {
  const { isAuthenticated, isLoading, logout, username } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/admin/login');
    }
  }, [isAuthenticated, isLoading, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0d0d0d] flex items-center justify-center">
        <i className="ri-loader-4-line text-[#C9A84C] text-3xl animate-spin"></i>
      </div>
    );
  }

  if (!isAuthenticated) return null;

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-[#f5f4f2] flex">
      {/* Sidebar */}
      <aside
        className={`${sidebarOpen ? 'w-60' : 'w-16'} bg-[#111111] flex flex-col transition-all duration-300 shrink-0 fixed top-0 left-0 h-full z-30`}
      >
        {/* Brand */}
        <div className="flex items-center gap-3 px-4 py-5 border-b border-white/5">
          <div className="w-8 h-8 flex items-center justify-center shrink-0">
            <i className="ri-shield-keyhole-line text-[#C9A84C] text-lg"></i>
          </div>
          {sidebarOpen && (
            <span className="text-white font-semibold text-sm tracking-wider truncate" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Ma Gádeni Admin
            </span>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 py-4 overflow-y-auto">
          {navItems.map((item) => {
            const active = location.pathname === item.path || (item.path !== '/admin' && location.pathname.startsWith(item.path));
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 mx-2 rounded-lg mb-0.5 transition-all cursor-pointer ${
                  active
                    ? 'bg-[#C9A84C]/15 text-[#C9A84C]'
                    : 'text-white/40 hover:text-white/70 hover:bg-white/5'
                }`}
              >
                <div className="w-5 h-5 flex items-center justify-center shrink-0">
                  <i className={`${item.icon} text-base`}></i>
                </div>
                {sidebarOpen && <span className="text-sm whitespace-nowrap">{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* Bottom */}
        <div className="border-t border-white/5 p-3 space-y-1">
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-white/30 hover:text-white/60 hover:bg-white/5 transition-all cursor-pointer"
          >
            <div className="w-5 h-5 flex items-center justify-center shrink-0">
              <i className="ri-external-link-line text-base"></i>
            </div>
            {sidebarOpen && <span className="text-sm whitespace-nowrap">Ver Site</span>}
          </a>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-white/30 hover:text-red-400 hover:bg-red-500/5 transition-all cursor-pointer"
          >
            <div className="w-5 h-5 flex items-center justify-center shrink-0">
              <i className="ri-logout-box-line text-base"></i>
            </div>
            {sidebarOpen && <span className="text-sm whitespace-nowrap">Sair</span>}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className={`flex-1 flex flex-col min-h-screen transition-all duration-300 ${sidebarOpen ? 'ml-60' : 'ml-16'}`}>
        {/* Top bar */}
        <header className="bg-white border-b border-gray-200 px-6 py-3.5 flex items-center justify-between sticky top-0 z-20">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className={`${sidebarOpen ? 'ri-menu-fold-line' : 'ri-menu-unfold-line'} text-gray-500 text-lg`}></i>
          </button>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#C9A84C]/15">
              <i className="ri-user-line text-[#C9A84C] text-sm"></i>
            </div>
            <span className="text-sm text-gray-600">{username}</span>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-6 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
